import React from 'react';
import { logError, formatError, getUserFriendlyMessage, isRecoverableError } from '../utils/errorTracking';

/**
 * Error boundary component with dev-only logging and retry
 * Catches unhandled React component errors
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorCount: number;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorCount: 0 };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error with context (dev only)
    logError(error, {
      component: errorInfo.componentStack?.split('\n')[0] || 'Unknown',
      action: 'React Error Boundary',
    });

    // Could send to error tracking service here
    // trackError(error, { componentStack: errorInfo.componentStack });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined, errorCount: this.state.errorCount + 1 });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const isDev = import.meta.env.DEV;
      const userMessage = getUserFriendlyMessage(this.state.error);
      const recoverable = isRecoverableError(this.state.error);

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-md p-8 space-y-6">
              {/* Error Icon */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4v2m0 4v2M12 3a9 9 0 100 18 9 9 0 000-18z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error Title & Message */}
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
                <p className="text-gray-600 text-sm">{userMessage}</p>
              </div>

              {/* Dev-only Error Details */}
              {isDev && (
                <div className="bg-gray-100 rounded p-4 max-h-40 overflow-auto">
                  <p className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-words">
                    {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <details className="mt-3">
                      <summary className="text-xs font-semibold text-gray-600 cursor-pointer hover:text-gray-800">
                        Stack Trace
                      </summary>
                      <pre className="mt-2 text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
                        {this.state.error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Retry Attempts Counter */}
              {isDev && this.state.errorCount > 0 && (
                <p className="text-xs text-yellow-600 text-center">
                  Retry attempt: {this.state.errorCount}
                </p>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {recoverable && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
                  >
                    Try Again
                  </button>
                )}
                <button
                  onClick={this.handleReload}
                  className={`w-full px-4 py-2 rounded-lg transition font-medium text-sm ${
                    recoverable
                      ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Reload Page
                </button>
                <a
                  href="/dashboard"
                  className="block text-center px-4 py-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  Back to Dashboard
                </a>
              </div>

              {/* Help Text */}
              <p className="text-xs text-gray-500 text-center">
                If the problem persists, please contact support or try again later.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
